import { logger } from "../default-logger";
import { dateRangeRegex, spanDisplayNames, spans } from '@/lib/dataTypes/span';
import dayjs from 'dayjs';

export const normalizeSearch = (search) => {
    if (!search) return "";
    const params = new URLSearchParams(search.replace(/^\//, ""));
    const obj = {};

    for (const [key, value] of params.entries()) {
      obj[key] = value;
    }

    return JSON.stringify(obj);
};

/**
 * Takes the url string (e.g. '?/proj=demo&appID=1234') and extracts the params into key value pairs
 * @returns a dictionary of params and their values
 */
export const extractURLParams = (searchParams, requestedURL) => {
    // If searchParams are present, extract using the searchParams object
    // if they're not detected (happens on safari) but location.searchParams exist, extract params from location instead
    let searchParamDict = null;
    if (searchParams?.keys().length > 0) {
      logger.debug('searchparams detected');
      searchParamDict = extractSearchParams(searchParams, ['proj', 'viewID', 'appID', 'params', 'alertID']);

    } else if (requestedURL) {
      logger.debug('searchparams: falling back to location object');
      searchParamDict = extractLocationSearchParams(requestedURL.search, ['proj', 'viewID', 'appID', 'params', 'alertID']);
    }
    logger.debug('extract params:', searchParamDict, requestedURL.search);

    return searchParamDict;
}

   /**
   * Extracts the URL params into a dictionary 
   * @param {URLSearchParams } searchParams - searchParams object, from react-router's useSearchParams hook 
   * @param {Array[String]} keysArray - sample input: ['proj', 'viewID', 'appID', 'params', 'qrKey']
   * @returns {Object, boolean} searchParamsDict - dictionary of url parameter keys and their values
   *  - If searchParams are invalid, returns false
   *  - If a key isn't found, returns {..., key:null, ...}
   */
export const extractSearchParams = (searchParams, keysArray) => {
    // // logger.debug('searchParams:', searchParams);
    if (searchParams.size > 0) {
        let searchParamDict = {};

        for (const key of keysArray) {
            let paramValue;
            let paramString = searchParams.get(key);
            if (key === 'params') {
                paramValue = {};
                // params are in the format: "{site:p:demo:r:2c122932-cff92ae2,span:thisWeek}"
                // remove curly braces
                paramString = paramString.replace(/{|}/g, '');

                // split by ',' but ignore chars that are between '[]'
                // e.g. "span:1,pts:[1,2,3]" outputs ["span:1", "pts:[1,2,3]"] instead of ["span:1", "pts:[1", "2", "3]"]
                // ?! means match the preceding char (,) only if it isn't followed by the following chars ([])
                let viewParamStrings = paramString.split(/,(?![^\[]*\])/g);
                for (const viewParam of viewParamStrings) {
                // extract key from before ':' and val from after ':'
                let key = viewParam.match('[a-z].*?:');
                let val = viewParam.match(':.*') // vals can contain ':', so just take the first ':' til the end

                // remove ':'
                if (key && val) {
                    key = key[0].replace(':', '');
                    val = val[0].replace(':', '');
                }

                // parse arrays
                logger.debug('cur param key', key, val)
                if (val.startsWith("[")) {
                    val = val.replace(/\[|\]/g, ''); // remove []
                    val = val.split(','); // split the string by ',' and create an array where each elem is a substring
                    logger.debug('cur param val', val)
                }

                // push to dict
                // ignore empty params (e.g. someone passes params={site:})
                if (val !== '') {
                    paramValue[`${key}`] = val;
                }

                }
            } else paramValue = paramString
            // push to dict
            searchParamDict[key] = paramValue;
        }

        return searchParamDict; 
    } else return false;
}

/**
 * 
 * @param {String} searchParams - From the location.search object, e.g. "?proj=demo&appID=@p:demo:...
 * @param {Array[String]} keysArray - sample input: ['proj', 'viewID', 'appID', 'params', 'qrKey'] 
 * use the keysArray so we only extract the relevant params
 * @returns {Object, boolean} searchParamsDict - dictionary of url parameter keys and their values
 *  - If a key isn't found, returns {..., key:null, ...}
 */
export const extractLocationSearchParams = (searchParams, keysArray) => {
    // split string into param key:val pairs: [proj=demo, appID=appID]
    const params = decodeURI(searchParams).replace('?', '').split('&');
    logger.debug('location.search', searchParams, params, decodeURI(searchParams));

    let searchParamDict = {};

    for (const key of keysArray) {

        const paramPair = params.find((paramPair) => paramPair.match(`${key}.*`));
        
        if (paramPair) {
          let paramValue;
          let paramString = paramPair.replace(`${key}=`, '');

          if (key === 'params') {
            paramValue = {};
            // params are in the format: "{site:p:demo:r:2c122932-cff92ae2,span:thisWeek}"
            // remove curly braces
            // paramString = paramString.replace(/{|}/g, '');
            paramString = paramString.replace(/{|}/g, '');

            // split by ',' but ignore chars that are between '[]'
            // e.g. "span:1,pts:[1,2,3]" outputs ["span:1", "pts:[1,2,3]"] instead of ["span:1", "pts:[1", "2", "3]"]
            // ?! means match the preceding char (,) only if it isn't followed by the following chars ([])
            let viewParamStrings = paramString.split(/,(?![^\[]*\])/g);
            logger.debug('param split', viewParamStrings)

            for (const viewParam of viewParamStrings) {
              // extract key from before ':' and val from after ':'
              let key = viewParam.match('[a-z].*?:');
              let val = viewParam.match(':.*') // vals can contain ':', so just take the first ':' til the end

              // remove ':'
              if (key && val) {
                  key = key[0].replace(':', '');
                  val = val[0].replace(':', '');
              }

              // check for array
              logger.debug('cur param key', key, val)
              if (val.startsWith("[")) {
                logger.debug('arr param:', val, key)
                val = val.replace(/\[|\]/g, '');
                val = val.split(',');
                logger.debug('cur param val', val)
              }

              // push to dict
              // ignore empty params (e.g. someone passes params={site:})
              if (val !== '') {
                paramValue[`${key}`] = val;
              }

            }
          } else paramValue = paramString

        

          // push to dict
          searchParamDict[key] = paramValue;
        } else searchParamDict[key] = null;
        
    }

    return searchParamDict;   
}

export const validateProject = (projectID, validProjects) => {
    // // logger.debug('proj info', projectID, validProjects);
    if (projectID === null || projectID === undefined) return null;
    if (!validProjects || validProjects.length === 0) return null;
    // validate inputs
    return validProjects.find(
        (project) => project.id.replace('p:', '') === projectID.replace('p:', '')
    );
}

export const validateViewParamValues = (params, viewParamData) => {
    logger.debug('start param data', params, viewParamData);
    let validParams = {};
    let validParamString = '';
    for (const paramKey in params) {
      let urlVal = params[paramKey].value ?? null;
      if (!urlVal) continue; // skip null vals (see /historian validation)
      logger.debug('cur url param:', paramKey, params, viewParamData, urlVal);
      // validate range
      if (paramKey === 'span' && params.span) {
        let spanValue = params.span.value || params.span;
        if (spanValue.match(dateRangeRegex)) {
          logger.debug('cur param: range', spanValue);
          let dates = spanValue?.split('..');
          let start = dayjs(dates[0]);
          let end = dayjs(dates[1]);

          if (start.isBefore(end)) {
            validParamString += `span:${spanValue},`;
            validParams[`span`] = {
              value: spanValue,
              dis: "Range",
            };
          }
          continue;
        } 
      }

      // find matching options
      let options = null;
      let defaultValue = null;
      for (const key in viewParamData) {
        let viewParam = viewParamData[key];
        logger.debug('cur view param', paramKey, viewParam, options, params[paramKey], key);
        if (options !== null) break;
        if (viewParam.name === paramKey) {
          logger.debug('found options', paramKey, viewParam)
          if (viewParam.options) {
            options = viewParam.options
          } else if (viewParam.enum) {
            options = viewParam.enum?.split(',');
            // options = enums.map((enum) => {})
          }
           
          defaultValue = viewParam.default || null;
          break;
        } else if (viewParam.childParams) {
          
          let childParams = viewParam.childParams;
          for (const child of childParams) {
            if (options !== null) break;
            if (child.name === paramKey) {
              logger.debug('in child params', paramKey, child.options, child.kind);
              options = child.options.map((option) => {return {
                ...option,
                kind: child.kind || null
              }});
              defaultValue = child.default || null;
              break;
            }
          }
        }
      }

      // check if the param value is in the options
      let paramValue = params[paramKey].value || params[paramKey];
      logger.debug('options to check', paramKey, options, defaultValue, paramValue, params[paramKey]);
      let newValue = null;

      if (Array.isArray(paramValue)) {
        newValue = [];
        let arrayParamString = "["
        for (const value of paramValue) {
          logger.debug('array start', value)
          let valueToCheck = typeof value === "object" ? value.value : value;
          let result = validateValue(options, valueToCheck, paramKey, viewParamData, defaultValue);
          logger.debug('array result', result)
          if (result) {
            newValue.push(result);
            arrayParamString += `${result.value},`
          }
        }
        arrayParamString = arrayParamString.slice(0, -1) + ']'; // remove the trailing comma
        // logger.debug('array new val', newValue, params[paramKey])
        if (newValue.length > 0) {
          const paramObj = params[paramKey];
          const newObj = {
              value: newValue,
              dis: paramObj.dis || paramObj.id?.dis || null,
              default: paramObj.default || null,
              name: paramObj.name || paramKey ,
              parentName: paramObj.parentName || null,
              kind: paramObj.kind || viewParamData[paramKey]?.kind || null,
              options: options ?? []
          };
          validParams[paramKey] = newObj;
          validParamString+= `${paramKey}:${arrayParamString},`; 
          logger.debug('array new val', newValue, newObj);
        }

      } else {
        newValue = validateValue(options, paramValue, paramKey, viewParamData, defaultValue);
        logger.debug('non array result', newValue)
        if (newValue !== null)  {
          validParams[paramKey] = newValue;
          let val = typeof newValue === "object" ? newValue.value : newValue;
          validParamString += `${paramKey}:${val},`; 
        }
      }
      logger.debug('check match', paramKey, newValue, validParams);
    }

    logger.debug('final param string', validParamString, validParams);

    if (validParamString.length > 0) {
      return {
        validParamString: '{' + validParamString.slice(0, -1) + '}', // remove the trailing comma
        validParams: validParams
      }
    } else return null;
}

export const validateValue = (options, paramValue, paramKey, viewParamData, defaultValue) => {
    logger.debug('start valid value', paramValue, options)
    let newValue = null;
    for (const option of options) {
        let value = option.value || option;
        logger.debug('cur view option', option, value, paramValue, paramKey, viewParamData);
        // if (option.id && option.id.val && option.id.val === paramValue) {
        if (value.replace(/@/g, '') === paramValue.replace(/@/g, '')) {
          logger.debug('match found start', paramValue, option);
          newValue = {
            value: paramValue,
            dis: option.dis || option.id?.dis || spanDisplayNames[paramValue] || paramValue,
            default: defaultValue,
            name: option.name || paramKey ,
            parentName: option.parentName || null,
            kind: option.kind || viewParamData[option.name || paramKey]?.kind || null
        };
          // validParams[paramKey] = newValue;
          logger.debug('match found final', option, paramValue, paramKey, newValue);
          return newValue;
        } 

    }

    return newValue;
}